import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  input,
  model,
} from '@angular/core';

/**
 * Reusable accordion-style section chrome for BrightNav tab content
 * (Finances, future tabs). Feature owns body and actions via content projection.
 */
@Component({
  selector: 'bn-collapsible-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './collapsible-section.component.html',
  styleUrl: './collapsible-section.component.scss',
})
export class CollapsibleSectionComponent {
  private readonly _platformId = inject(PLATFORM_ID);
  private _restoredPersistKey: string | null = null;

  /** Section heading. */
  readonly title = input.required<string>();

  /** Optional PrimeIcons class (e.g. `pi pi-chart-pie`). */
  readonly icon = input<string | null>(null);

  /** Accessible name for the section; defaults to title. */
  readonly ariaLabel = input<string | null>(null);

  /** Whether the body is shown. Two-way bindable. */
  readonly expanded = model(true);

  /** Optional local-storage key used to restore the expanded state. */
  readonly persistKey = input<string | null>(null);

  readonly resolvedAriaLabel = computed(
    () => this.ariaLabel()?.trim() || this.title(),
  );

  constructor() {
    effect(() => {
      const key = this.persistKey()?.trim() || null;
      const expanded = this.expanded();
      if (!key || !isPlatformBrowser(this._platformId)) return;

      try {
        if (this._restoredPersistKey !== key) {
          this._restoredPersistKey = key;
          const stored = localStorage.getItem(key);
          if (stored === 'true' || stored === 'false') {
            this.expanded.set(stored === 'true');
            return;
          }
        }
        localStorage.setItem(key, String(expanded));
      } catch {
        // Storage may be unavailable; collapse behavior still works in-memory.
      }
    });
  }

  toggleExpanded(): void {
    this.expanded.update((v) => !v);
  }
}
